<script setup lang="ts">
  import { computed } from "vue";
  import { RefreshCw } from "lucide-vue-next";
  import { storeToRefs } from "pinia";
  import AiStar from "@/components/episto-ui/ai/AiStar.vue";
  import { Badge } from "@/components/ui/badge";
  import { Button } from "@/components/ui/button";
  import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
  import { useGridsStore } from "@stores/survey/grids/grids";
  import { renderMarkdown } from "@utils/renderMarkdown.js";
  import GridSummarySegments from "./GridSummarySegments.vue";
  import type { GridCell, GridSummary } from "@/types";

  const answer = defineModel<{ prompt: string; cell?: GridCell; summary?: GridSummary } | null>("answer", {
    required: true,
  });

  const gridsStore = useGridsStore();
  const { currentGrid } = storeToRefs(gridsStore);

  const refresh = async (summary: GridSummary) => {
    await gridsStore.refreshSummary(summary.grid_column_id);
    answer.value = null;
  };

  const isOpen = computed({
    get: () => answer.value !== null,
    set: (value: boolean) => {
      if (!value) answer.value = null;
    },
  });
</script>

<template>
  <Sheet v-model:open="isOpen">
    <SheetContent
      side="right"
      class="flex w-[40vw] min-w-[420px] flex-col gap-0 overflow-hidden p-0 sm:max-w-[40vw]">
      <div class="shrink-0 border-b px-6 pb-4 pt-5">
        <SheetTitle class="heading-title pr-8 leading-snug">{{ answer?.prompt }}</SheetTitle>
        <template v-if="answer?.summary">
          <div class="mt-2 flex items-center gap-2 text-13">
            <AiStar class="size-3.5" />
            <span class="font-medium">Summary</span>
            <Badge
              variant="secondary"
              class="font-normal">
              {{ currentGrid?.rows.length }} populations
            </Badge>
          </div>
          <div
            v-if="answer.summary.outdated || answer.summary.status === 'failed'"
            class="mt-2 flex items-center gap-2 text-xs text-status-warning-strong">
            <span>
              {{
                answer.summary.status === "failed"
                  ? "This summary failed."
                  : "Generated before the populations of this grid last changed."
              }}
            </span>
            <Button
              variant="outline"
              size="sm"
              @click="refresh(answer.summary)">
              <RefreshCw class="size-3.5" />
              Refresh
            </Button>
          </div>
        </template>
      </div>

      <div class="min-h-0 flex-1 space-y-7 overflow-y-auto px-6 py-5">
        <section v-if="answer?.summary">
          <h3 class="heading-label mb-2">Across populations</h3>
          <p class="text-sm leading-relaxed text-foreground">
            <GridSummarySegments :segments="answer.summary.segments" />
          </p>
        </section>
        <section v-if="answer?.cell">
          <h3 class="heading-label mb-2">Answer</h3>
          <div
            class="prose prose-sm max-w-none text-sm leading-relaxed text-foreground"
            v-html="renderMarkdown(answer.cell.answer)" />
        </section>
      </div>
    </SheetContent>
  </Sheet>
</template>
